<script setup lang="ts">
import { computed, nextTick, onMounted, provide, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Ref } from 'vue';
import { getCustomFieldTestId } from '@nova/core';
import type { IWarehouse } from '@nova/core';
import type { CheckoutFormFieldWithTrigger } from '@/composables/useCheckoutCustomForms';
import { CUSTOM_FORMS_STORAGE_CONTEXT_KEY } from '@satellite/composables/useCustomFormsStorageContext';

import NextStep from '@/components/NextStep.vue';
import { useCheckoutCustomForms } from '@/composables/useCheckoutCustomForms';
import { CheckoutFlowStep, useCheckoutFlow } from '@/composables/useCheckoutFlow';
import { useWarehouseInfo } from '@/composables/useWarehouseInfo';
import useNotify from '@/composables/useNotify';
import { useCheckoutAnalytics } from '@/composables/useCheckoutAnalytics';
import checkoutService from '@/services/checkoutService';
import { syncGlobalSeoLocale, type SupportedLocale } from '@/lib/junoLocale';

const checkoutFlow = useCheckoutFlow();
const { warehouseData } = useWarehouseInfo();
const { notify } = useNotify();
const { trackCheckoutDetailsPageCompleted } = useCheckoutAnalytics();
const { t, locale } = useI18n();
const checkoutApi = checkoutService();

// Storage context for document/multi-document custom form fields (public checkout endpoint)
const customFormsStorageContext = computed(() => ({
  warehouseId: warehouseData.value?.id ?? '',
  storageEndpoint: 'checkout/public/storage'
}));
provide(CUSTOM_FORMS_STORAGE_CONTEXT_KEY, customFormsStorageContext);

const {
  hasCheckoutCustomForms,
  formFieldsWithTrigger,
  getFormFieldValue,
  setFormFieldValue,
  getCustomField,
  customFormsComponentsMapping,
  isFormValid,
  prepareSubmitPayload,
  timezone,
  militaryTimeEnabled
} = useCheckoutCustomForms({
  warehouse: warehouseData as Ref<Partial<IWarehouse> | null | undefined>,
  assetVisitId: checkoutFlow.assetVisitId,
  onNotify: (message, type) => notify(message, type === 'warning' ? 'warn' : type)
});

watch(
  isFormValid,
  valid => {
    checkoutFlow.setStepValid(CheckoutFlowStep.CheckoutDetailsPage, valid);
  },
  { immediate: true }
);

// Custom-form fields (labels/descriptions) are backend content translated by GlobalSEO, not
// vue-i18n. GlobalSEO's own script only scans the DOM once on initial page load, but these
// fields render asynchronously after that pass — so without an explicit resync they never get
// translated unless the user happens to also switch languages via the selector.
const resyncGlobalSeoForCustomFields = async () => {
  if (!hasCheckoutCustomForms.value) {
    return;
  }
  await nextTick();
  await syncGlobalSeoLocale(locale.value as SupportedLocale);
  console.log('Resynced GlobalSEO translations for custom form fields in language:', locale.value);
};

onMounted(resyncGlobalSeoForCustomFields);
watch(hasCheckoutCustomForms, resyncGlobalSeoForCustomFields);

const handleNext = async () => {
  checkoutFlow.setIsStepLoading(true);
  try {
    const warehouseId = warehouseData.value?.id;
    const appointmentId = checkoutFlow.appointmentId.value;
    const assetVisitId = checkoutFlow.assetVisitId.value;
    if (!warehouseId || !assetVisitId) {
      notify(t('checkoutDetailsPage.errors.missingData'), 'error');
      return;
    }

    const bolRequired = warehouseData.value?.settings?.checkoutBolSignedRequired;
    if (bolRequired) {
      if (hasCheckoutCustomForms.value) {
        checkoutFlow.setPendingCustomFormsPayload(prepareSubmitPayload());
      }
      checkoutFlow.setNextStep(CheckoutFlowStep.ViewBolPage);
      return;
    }

    const customFormsData = hasCheckoutCustomForms.value ? prepareSubmitPayload() : undefined;
    await checkoutApi.completeCheckout(warehouseId, {
      assetVisitId,
      appointmentId,
      ...(customFormsData?.length ? { customFormsData } : {})
    });

    trackCheckoutDetailsPageCompleted();

    checkoutFlow.setNextStep(CheckoutFlowStep.CheckoutCompletePage);
  } catch (error) {
    notify(t('checkoutDetailsPage.errors.saveFailed'), 'error');
    console.error('Checkout details step failed:', error);
  } finally {
    checkoutFlow.setIsStepLoading(false);
  }
};

const formFieldsWithCustomField = computed(() =>
  formFieldsWithTrigger.value.map((item: CheckoutFormFieldWithTrigger) => ({
    ...item,
    customField: getCustomField(item.formField)
  }))
);

function getFieldComponent(formField: (typeof formFieldsWithTrigger.value)[0]['formField']) {
  const type = formField.field?.type;
  return type ? customFormsComponentsMapping.value[type]?.component : null;
}
</script>

<template>
  <div class="d-flex flex-column gap-6 px-4 fill-height">
    <v-btn
      variant="text"
      prepend-icon="mdi-arrow-left"
      class="align-self-start juno-i18n-exclude"
      @click="checkoutFlow.setPreviousStep(CheckoutFlowStep.ValidationPage)">
      {{ t('common.actions.back') }}
    </v-btn>

    <template v-if="hasCheckoutCustomForms">
      <h3 class="heading-md-bold juno-i18n-exclude">
        {{ t('checkoutDetailsPage.additionalDetailsHeading') }}
      </h3>
      <div class="checkout-form-body d-flex flex-column gap-3">
        <div
          v-for="item in formFieldsWithCustomField"
          :key="`${item.triggerId}-${item.formField.id}-${locale}`"
          class="d-flex flex-column gap-2 form-field">
          <v-label
            v-if="item.customField.label"
            class="typography-body-sm-bold"
            :class="{ 'required-field': item.customField.required }">
            {{ item.customField.label }}
            <span v-if="item.customField.description" class="custom-field__help">
              <v-tooltip location="top">
                <template #activator="{ props }">
                  <v-icon v-bind="props" size="small">mdi-help-circle</v-icon>
                </template>
                <span>{{ item.customField.description }}</span>
              </v-tooltip>
            </span>
          </v-label>
          <component
            :is="getFieldComponent(item.formField)"
            v-if="getFieldComponent(item.formField)"
            :field="item.customField"
            :outlined="true"
            :timezone="timezone"
            :military-time-enabled="militaryTimeEnabled"
            :model-value="getFormFieldValue(item.formField.id)"
            :test-id="
              getCustomFieldTestId({ formFieldId: item.formField.id, triggerId: item.triggerId })
            "
            @update:model-value="(v: unknown) => setFormFieldValue(item.formField.id, v)" />
        </div>
      </div>
    </template>
    <template v-else>
      <h3 class="heading-md-bold juno-i18n-exclude">
        {{ t('checkoutDetailsPage.checkoutDetailsHeading') }}
      </h3>
      <p class="juno-i18n-exclude text-body-1 text-grey">
        {{ t('checkoutDetailsPage.noFormsMessage') }}
      </p>
    </template>
  </div>

  <next-step
    :is-step-valid="checkoutFlow.isStepValid(CheckoutFlowStep.CheckoutDetailsPage)"
    :on-step-complete="handleNext" />
</template>

<style scoped lang="scss">
.checkout-form-body {
  border-radius: 4px;
  border: 1px solid $color-line-border;
  padding: $spacing-4;
}

.checkout-form-body .form-field {
  .v-input {
    margin-top: 0;
  }

  .v-label {
    position: relative;
    color: $color-text-secondary;
    align-self: flex-start;
    opacity: 1;
  }
}

.checkout-form-body .form-field .required-field {
  &:after {
    content: '*';
    color: $color-text-error;
  }
}

.custom-field__help {
  margin-left: 4px;
  vertical-align: middle;
}
</style>
